// The account commands, and the one place that remembers who is signed in.
//
// Every call is one question on its own connection: none of this needs the game connection, and
// signing in happens when there is no room to belong to. The server decides everything — this only
// carries the answer around and keeps the session token where the next call can find it.
//
// A refused command arrives as an error and comes back out of here as a rejected promise carrying
// the server's own sentence, which is what the screens show.

import { ask as defaultAsk } from './request.js';
import { Protocol, ServerProtocol } from '../generated/protocol.js';
import { accountToken, sessionToken, setSessionToken } from './identity.js';

export class Accounts extends EventTarget {
  // { userName, email, emailVerified, emailEnabled, claimableGames } while signed in, null when not
  #account = null;
  // How a question reaches the server. Only a test ever passes a different one, and that is the
  // point: everything below is about what the answers mean, not about sockets
  #ask;

  constructor(ask = defaultAsk) {
    super();
    this.#ask = ask;
  }

  get account() {
    return this.#account;
  }

  get signedIn() {
    return this.#account !== null;
  }

  get userName() {
    return this.#account?.userName ?? '';
  }

  // Whether a session token is even worth trying: a browser that never signed in skips the
  // question entirely and shows the entry form at once
  get hasStoredSession() {
    return Boolean(sessionToken());
  }

  // Called after every change, so the lobby and the account screen both redraw from one place
  #changed() {
    this.dispatchEvent(new CustomEvent('account', { detail: { account: this.#account } }));
  }

  #adopt(reply) {
    setSessionToken(reply[ServerProtocol.SessionTokenID] ?? '');
    this.#account = {
      userName: reply[ServerProtocol.UserNameID] ?? '',
      email: reply[ServerProtocol.EmailID] ?? '',
      emailVerified: reply[ServerProtocol.EmailVerifiedID] === true,
      emailEnabled: reply[ServerProtocol.EmailEnabledID] === true,
      claimableGames: reply[ServerProtocol.ClaimableGamesID] ?? 0,
    };
    this.#changed();
    return this.#account;
  }

  #forget() {
    setSessionToken('');
    this.#account = null;
    this.#changed();
  }

  // Both tags are answers: auth_ok changes who is signed in, auth_done only reports
  #send(serverUrl, tag, fields) {
    return this.#ask(
      serverUrl,
      { [Protocol.JSONTagID]: tag, ...fields },
      [ServerProtocol.TagAuthOk, ServerProtocol.TagAuthDone],
    );
  }

  // At start-up: the stored token, if it still means anything. A token the server no longer knows
  // is dropped here rather than kept around to fail later at the table
  async resume(serverUrl) {
    if (!sessionToken()) return null;
    try {
      return this.#adopt(
        await this.#send(serverUrl, ServerProtocol.TagResumeSession, {
          [ServerProtocol.SessionTokenID]: sessionToken(),
          [ServerProtocol.AccountTokenID]: accountToken(),
        }),
      );
    } catch (error) {
      // Expired or revoked: play on as a guest rather than blocking the entry screen
      this.#forget();
      return null;
    }
  }

  async register(serverUrl, { userName, password, email }) {
    return this.#adopt(
      await this.#send(serverUrl, ServerProtocol.TagRegister, {
        [ServerProtocol.UserNameID]: userName,
        [ServerProtocol.PasswordID]: password,
        [ServerProtocol.EmailID]: email ?? '',
        // The games this browser played as a guest come along with the registration
        [ServerProtocol.AccountTokenID]: accountToken(),
      }),
    );
  }

  async login(serverUrl, { userName, password }) {
    return this.#adopt(
      await this.#send(serverUrl, ServerProtocol.TagLogin, {
        [ServerProtocol.UserNameID]: userName,
        [ServerProtocol.PasswordID]: password,
        [ServerProtocol.AccountTokenID]: accountToken(),
      }),
    );
  }

  async logout(serverUrl) {
    const token = sessionToken();
    // Forgotten here first: whatever the server answers, this browser is signed out
    this.#forget();
    if (!token) return;
    try {
      await this.#send(serverUrl, ServerProtocol.TagLogout, {
        [ServerProtocol.SessionTokenID]: token,
      });
    } catch {
      // An unreachable server does not get to keep somebody signed in on this device
    }
  }

  async changePassword(serverUrl, { password, newPassword }) {
    const reply = await this.#send(serverUrl, ServerProtocol.TagChangePassword, {
      [ServerProtocol.SessionTokenID]: sessionToken(),
      [ServerProtocol.PasswordID]: password,
      [ServerProtocol.NewPasswordID]: newPassword,
    });
    return reply[ServerProtocol.MessageID] ?? '';
  }

  async setEmail(serverUrl, email) {
    const reply = await this.#send(serverUrl, ServerProtocol.TagSetEmail, {
      [ServerProtocol.SessionTokenID]: sessionToken(),
      [ServerProtocol.EmailID]: email,
    });
    const note = reply[ServerProtocol.MessageID] ?? '';
    this.#adopt(reply);
    return note;
  }

  async requestPasswordReset(serverUrl, who) {
    const reply = await this.#send(serverUrl, ServerProtocol.TagRequestPasswordReset, {
      [ServerProtocol.UserNameID]: who,
    });
    return reply[ServerProtocol.MessageID] ?? '';
  }

  async resetPassword(serverUrl, { resetToken, newPassword }) {
    const reply = await this.#send(serverUrl, ServerProtocol.TagResetPassword, {
      [ServerProtocol.ResetTokenID]: resetToken,
      [ServerProtocol.NewPasswordID]: newPassword,
    });
    // Every session went out with the reset, this one included
    this.#forget();
    return reply[ServerProtocol.MessageID] ?? '';
  }

  async verifyEmail(serverUrl, verifyToken) {
    const reply = await this.#send(serverUrl, ServerProtocol.TagVerifyEmail, {
      [ServerProtocol.VerifyTokenID]: verifyToken,
    });
    // The profile on screen still says "da confermare": ask for it again if we are signed in
    await this.resume(serverUrl);
    return reply[ServerProtocol.MessageID] ?? '';
  }

  async claimHistory(serverUrl) {
    const reply = await this.#send(serverUrl, ServerProtocol.TagClaimHistory, {
      [ServerProtocol.SessionTokenID]: sessionToken(),
      [ServerProtocol.AccountTokenID]: accountToken(),
    });
    if (this.#account) {
      this.#account = { ...this.#account, claimableGames: 0 };
      this.#changed();
    }
    return reply[ServerProtocol.MessageID] ?? '';
  }

  async deleteAccount(serverUrl, password) {
    const reply = await this.#send(serverUrl, ServerProtocol.TagDeleteAccount, {
      [ServerProtocol.SessionTokenID]: sessionToken(),
      [ServerProtocol.PasswordID]: password,
    });
    this.#forget();
    return reply[ServerProtocol.MessageID] ?? '';
  }
}

// The two links that arrive by mail land on the normal page with a parameter. Reading them once at
// start-up, and wiping them from the address bar, keeps a reset link out of the browser history
export function linkFromAddress() {
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  const reset = params.get('reset');
  const verify = params.get('verify');
  if (!reset && !verify) return null;
  try {
    const clean = new URL(globalThis.location.href);
    clean.searchParams.delete('reset');
    clean.searchParams.delete('verify');
    globalThis.history?.replaceState({}, '', clean.pathname + clean.search + clean.hash);
  } catch {
    // An address bar that cannot be rewritten is not a reason to refuse the link
  }
  return reset ? { kind: 'reset', token: reset } : { kind: 'verify', token: verify };
}
